import * as React from 'react'
import { ReactElement, RefObject, useEffect, useMemo, useRef } from 'react'
import { DialogContent, makeStyles, mergeClasses, MessageBar, MessageBarBody, tokens } from '@fluentui/react-components'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { HammockFormDialogProps } from '../HammockFormDialog'
import * as Yup from 'yup'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import { FixedPriceMutationDto, FixedPriceOverviewDto } from '../../../../config/registrationConfig'
import { BaseMutableOverviewDialogProps } from '../../../../pages/BaseMutableOverview/BaseMutableOverview'
import { InvoiceStatus } from '../../../../config/finance/invoiceConfig'
import { useFormikContext } from 'formik'
import FormikTextareaField from '../../../../components/Formik/FormikTextareaField'
import UserCombobox from '../../../../components/Combobox/UserCombobox'
import useRoles, { rolesWriteAllTimes } from '../../../../hooks/useRoles'
import { SubComboboxProps } from '../../../../components/Combobox/BaseCombobox'
import { UserListItemDto } from '../../../../config/userConfig'
import LocalDatePicker, { LocalDatePickerProps } from '../../../../components/DatePicker/LocalDatePicker'
import FormikField, { useFormikFieldStyles } from '../../../../components/Formik/FormikField'
import { usePrevious } from '@fluentui/react-hooks'
import { IRefObject } from '@fluentui/utilities'
import { ITextField } from '@fluentui/react'
import useCurrency from '../../../../hooks/useCurrency'
import FormikMoneyField from '../../../../components/Formik/FormikMoneyField'
import FormContainer from '../../Form/FormContainer'
import useFixedPriceTypes from '../../../../hooks/useFixedPriceTypes'
import FixedPriceTypeCombobox, { FixedPriceTypeComboboxProps } from '../../../../components/Combobox/FixedPriceTypeCombobox'
import VatTypeCombobox from '../../../../components/Combobox/i18n/VatTypeCombobox'

const useStyles = makeStyles({
    input: {
        minWidth: '80px',
    },
    billable: {
        flexGrow: 2,
    },
    rateRow: {
        display: 'grid',
        gridTemplateColumns: `1fr 1fr 1fr`,
        gap: tokens.spacingHorizontalS,
    },
    dateRow: {
        display: 'grid',
        gridTemplateColumns: `1fr 2fr`,
        gap: tokens.spacingHorizontalS,
    },
    dateRowSmall: {
        display: 'grid',
        gridTemplateColumns: `1fr 1fr`,
        gap: tokens.spacingHorizontalS,
    },
})

export const getValidationSchema = (translate: (key: string, options?: any) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        fileId: Yup.string().uuid().label(translate('registration.fileid')).required(),
        userId: Yup.string().uuid().label(translate('registration.userid')).required(),
        type: Yup.string().uuid().label(translate('fixedprice.type')).required(),
        name: Yup.string().label(translate('fixedprice.name')).required(),
        description: Yup.string().label(translate('registration.description')).required(),
        rate: Yup.string().label(translate('registration.rate')).required(),
        date: Yup.string().label(translate('registration.date')).required(),
    })
}

export default function FixedPriceMutationDialogBodyContent(
    props: HammockFormDialogProps<FixedPriceMutationDto, BaseMutableOverviewDialogProps<FixedPriceOverviewDto>> & {
        focusTrigger?: number
        updateFocusTrigger?: () => void
    },
): ReactElement {
    const roles = useRoles()
    const styles = useStyles()
    const formikFieldStyles = useFormikFieldStyles()
    const { t: translate } = useTranslation()
    const { isViewport, viewport } = useViewport()

    const { initialValues, values, setValues, setFieldValue } = useFormikContext<FixedPriceMutationDto>()
    const previousValues = usePrevious<FixedPriceMutationDto>(values)

    const dialogBodyProps = useMemo(() => props.dialogFormBodyProps?.() ?? ({} as BaseMutableOverviewDialogProps<FixedPriceOverviewDto>), [props])

    const viewportSmall = useMemo<boolean>(() => isViewport('<', Viewport.OutlookNew), [viewport])

    const isInvoiceDraft: boolean = dialogBodyProps.item?.invoice?.status === InvoiceStatus.Draft || false
    const isInvoiceFinal: boolean =
        dialogBodyProps.item?.invoice?.status !== undefined && dialogBodyProps.item?.invoice?.status !== InvoiceStatus.Draft

    const { currency } = useCurrency(values.currency)
    const currencySymbol = useMemo<string>(() => currency?.symbol ?? currency?.alpha ?? '¤', [currency])

    const { loaded: loadedTypes, fixedPriceTypes } = useFixedPriceTypes()

    //Check if the category field should use the initial value or live value
    const useInitialValues = useMemo<boolean>(() => !previousValues, [initialValues, previousValues])

    const typeFocusRef: IRefObject<HTMLInputElement> = useRef<HTMLInputElement>()
    const descriptionFocusRef: RefObject<ITextField> = useRef<ITextField>()

    useEffect(() => {
        if (isInvoiceDraft || isInvoiceFinal) {
            return
        }

        if (!values.type) {
            typeFocusRef.current?.focus()
        } else if (!!initialValues.description || !values.description) {
            //Focus on description when in edit mode or when empty in create mode.
            descriptionFocusRef.current?.focus()
        }
    }, [values.type, props.focusTrigger])

    useEffect(() => {
        props.setValidationSchema?.(getValidationSchema(translate))
    }, [])

    useEffect(() => {
        let name = values.name
        let description = values.description
        let longDescription = values.longDescription
        if (useInitialValues) {
            name = initialValues.name
            description = initialValues.description
            longDescription = initialValues.longDescription
        }

        setFieldValue('name', name)
        setFieldValue('description', description)
        setFieldValue('longDescription', longDescription)
    }, [fixedPriceTypes, values.type])

    return (
        <DialogContent>
            <FormContainer>
                {isInvoiceDraft && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>{translate('fixedprice.edit.form.invoice.draft')}</MessageBarBody>
                    </MessageBar>
                )}
                {isInvoiceFinal && (
                    <MessageBar intent={'error'} layout={'multiline'}>
                        <MessageBarBody>{translate('fixedprice.edit.form.invoice.final')}</MessageBarBody>
                    </MessageBar>
                )}
                <FormikComboboxField<FixedPriceMutationDto, FixedPriceTypeComboboxProps>
                    required={loadedTypes && fixedPriceTypes?.length > 0}
                    fieldName={'type'}
                    component={FixedPriceTypeCombobox}
                    componentProps={{
                        required: true,
                        selectedOptions: [values.type],
                        currency: values.currency,
                        date: values.date,
                        onOptionSelect: (_, selectedOptions, selectedEntities) => {
                            const newFixedPriceType = selectedEntities?.[0]

                            setValues(
                                oldValues => ({
                                    ...oldValues,
                                    type: selectedOptions?.[0],
                                    vatType: newFixedPriceType?.vatType,
                                    name: newFixedPriceType?.name,
                                    description: newFixedPriceType?.description,
                                    longDescription: newFixedPriceType?.longDescription,
                                    rate: (newFixedPriceType?.includingVat ? undefined : newFixedPriceType?.rate) ?? '0.00',
                                    rateTotal: newFixedPriceType?.includingVat ? newFixedPriceType?.rate : undefined,
                                }),
                                false,
                            )
                        },
                        onKeyDown: e => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                props.updateFocusTrigger()
                            }
                        },
                    }}
                />
                <FormikTextareaField<FixedPriceMutationDto>
                    fieldName="description"
                    componentProps={{
                        required: true,
                        readOnly: isInvoiceFinal,
                        disabled: isInvoiceFinal,
                        placeholder: 'Omschrijving',
                        ref: descriptionFocusRef,
                    }}
                    className={formikFieldStyles.tinyLabel}
                />
                <FormikComboboxField<FixedPriceMutationDto, SubComboboxProps<UserListItemDto>>
                    required
                    fieldName={'userId'}
                    component={UserCombobox}
                    componentProps={{
                        readOnly: isInvoiceFinal || !roles.any(rolesWriteAllTimes),
                        disabled: isInvoiceFinal || !roles.any(rolesWriteAllTimes),
                        onKeyDown: e => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                props.updateFocusTrigger()
                            }
                        },
                    }}
                />
                <div className={styles.rateRow}>
                    <FormikMoneyField<FixedPriceMutationDto>
                        required
                        fieldName={'rate'}
                        componentProps={{
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                            contentBefore: currencySymbol,
                            className: styles.input,
                        }}
                    />
                    <FormikComboboxField
                        required
                        fieldName={'vatType'}
                        component={VatTypeCombobox}
                        componentProps={{
                            onOptionSelect: (_event, _selectedOptions, selectedEntities) => {
                                setFieldValue('vatType', selectedEntities?.[0])
                            },
                        }}
                    />

                    <FormikMoneyField<FixedPriceMutationDto>
                        required
                        fieldName={'rateTotal'}
                        componentProps={{
                            readOnly: true,
                            disabled: isInvoiceFinal,
                            tabIndex: -1,
                            appearance: 'filled-darker',
                            contentBefore: currencySymbol,
                            className: styles.input,
                        }}
                    />
                </div>
                <div className={mergeClasses(styles.dateRow, viewportSmall && styles.dateRowSmall)}>
                    <FormikField<LocalDatePickerProps, FixedPriceMutationDto>
                        required
                        fieldName={'date'}
                        component={LocalDatePicker}
                        componentProps={{
                            isoValue: values.date,
                            onSelectDate: date => setFieldValue('date', date),
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                            root: { className: styles.input },
                        }}
                    />
                </div>
                {/*Source - https://stackoverflow.com/a/477699*/}
                <input type="submit" hidden />
            </FormContainer>
        </DialogContent>
    )
}
